/*
 * rc-coming-soon-sections.js
 * --------------------------------------------------------------------------
 * One lightweight controller for BOTH Coming Soon sections (hero + showcase).
 *
 * Responsibilities:
 *   - Scroll-reveal via IntersectionObserver (NO scroll-event loops).
 *   - Pause/resume autoplay <video> when off/on screen.
 *   - Hover media swap on hover-capable pointers only.
 *   - Defer external iframes (YouTube/Vimeo) until near viewport / click.
 *   - Theme-editor lifecycle: section load/unload, block select/deselect.
 *   - prefers-reduced-motion: reveal everything immediately, no autoplay.
 *
 * Hard rule: content is ALWAYS visible if JS never runs. The reveal classes
 * only ADD motion; the base CSS shows content. We add `.rc-cs-js` to <html>
 * (below) so the "hidden-before-reveal" styles apply ONLY when JS is alive.
 *
 * No external dependencies. Safe to load once; guards against double-init.
 * --------------------------------------------------------------------------
 */
(function () {
  'use strict';

  if (window.__rcComingSoonInit) {
    // Script somehow loaded twice — re-run section binding only.
    window.__rcComingSoon.bindAll();
    return;
  }
  window.__rcComingSoonInit = true;

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
  var HOVER_OK = window.matchMedia('(hover: hover) and (pointer: fine)');

  // Mark the document so CSS can safely pre-hide reveal targets only when JS runs.
  document.documentElement.classList.add('rc-cs-js');
  if (REDUCED.matches) document.documentElement.classList.add('rc-cs-reduced');

  // ---- Reveal observer (shared) -------------------------------------------
  var revealObserver = null;
  function getRevealObserver() {
    if (revealObserver) return revealObserver;
    revealObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var el = entry.target;
          el.classList.add('rc-cs-in');
          // Replay support: if the section opts into replay, keep observing.
          var replay = el.closest('[data-rc-cs-replay="true"]');
          if (!replay) revealObserver.unobserve(el);
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -8% 0px' }
    );
    return revealObserver;
  }

  // ---- Video viewport pause/play ------------------------------------------
  var videoObserver = null;
  function getVideoObserver() {
    if (videoObserver) return videoObserver;
    videoObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var v = entry.target;
          if (REDUCED.matches) return; // never autoplay under reduced motion
          if (v.dataset.rcPauseOffscreen === 'false') return;
          if (entry.isIntersecting) {
            if (v.dataset.rcAutoplay === 'true') {
              var p = v.play();
              if (p && p.catch) p.catch(function () {});
            }
          } else if (!v.paused) {
            v.pause();
          }
        });
      },
      { threshold: 0.25 }
    );
    return videoObserver;
  }

  // ---- Deferred external embeds (YouTube/Vimeo) ---------------------------
  var embedObserver = null;
  function getEmbedObserver() {
    if (embedObserver) return embedObserver;
    embedObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          loadEmbed(entry.target);
          embedObserver.unobserve(entry.target);
        });
      },
      { rootMargin: '200px 0px' }
    );
    return embedObserver;
  }

  function loadEmbed(holder) {
    if (holder.dataset.rcEmbedLoaded === 'true') return;
    var src = holder.getAttribute('data-rc-embed-src');
    if (!src) return;
    var iframe = document.createElement('iframe');
    iframe.setAttribute('src', src);
    iframe.setAttribute('frameborder', '0');
    iframe.setAttribute(
      'allow',
      'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
    );
    iframe.setAttribute('allowfullscreen', '');
    iframe.setAttribute('loading', 'lazy');
    iframe.setAttribute('title', holder.getAttribute('data-rc-embed-title') || 'Video');
    iframe.className = 'rc-cs-embed__iframe';
    holder.appendChild(iframe);
    holder.dataset.rcEmbedLoaded = 'true';
  }

  // ---- Hover media swap ----------------------------------------------------
  function wireHover(card) {
    if (!HOVER_OK.matches) return; // hover-capable + fine pointer only
    if (card.dataset.rcHoverWired === 'true') return;
    var hoverVideo = card.querySelector('[data-rc-hover-video]');
    if (!hoverVideo) return;
    card.addEventListener('mouseenter', function () {
      if (REDUCED.matches) return;
      var p = hoverVideo.play();
      if (p && p.catch) p.catch(function () {});
    });
    card.addEventListener('mouseleave', function () {
      hoverVideo.pause();
      try {
        hoverVideo.currentTime = 0;
      } catch (e) {}
    });
    card.dataset.rcHoverWired = 'true';
  }

  // ---- Per-section binding -------------------------------------------------
  function bindSection(root) {
    if (!root || root.dataset.rcCsBound === 'true') return;
    root.dataset.rcCsBound = 'true';

    var ro = getRevealObserver();
    root.querySelectorAll('[data-rc-cs-reveal]').forEach(function (el) {
      if (REDUCED.matches) {
        el.classList.add('rc-cs-in'); // show immediately, no motion
      } else {
        ro.observe(el);
      }
    });

    var vo = getVideoObserver();
    root.querySelectorAll('video[data-rc-managed]').forEach(function (v) {
      vo.observe(v);
    });

    var eo = getEmbedObserver();
    root.querySelectorAll('[data-rc-embed-src]').forEach(function (holder) {
      // Click-to-load buttons load immediately on activation.
      var btn = holder.querySelector('[data-rc-embed-play]');
      if (btn) {
        btn.addEventListener('click', function () {
          loadEmbed(holder);
        });
      }
      eo.observe(holder);
    });

    root.querySelectorAll('[data-rc-cs-card]').forEach(wireHover);
  }

  function bindAll() {
    document.querySelectorAll('[data-rc-cs-section]').forEach(bindSection);
  }

  window.__rcComingSoon = { bindAll: bindAll, bindSection: bindSection };

  // ---- Dealer PRE-BUY -> open the store's real quick-order modal ----------
  // Reuses the site-wide `.b2b-modal-overlay` + openB2BModal() pattern (the
  // same one snippets/card-product.liquid uses). We add the accessibility the
  // base pattern lacks: focus into the dialog, Escape to close, focus restore.
  var rcCsOpener = null;
  var rcCsActiveModal = null;
  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function rcCsOpenModal(modalId, opener) {
    var modal = document.getElementById(modalId);
    if (!modal) return;
    rcCsOpener = opener || null;
    rcCsActiveModal = modal;
    if (typeof window.openB2BModal === 'function') {
      window.openB2BModal(modalId);
    } else {
      document.body.appendChild(modal);
      modal.style.display = 'block';
      document.body.style.overflow = 'hidden';
    }
    // Move focus to the first focusable control inside the dialog.
    var first = modal.querySelector(FOCUSABLE);
    if (first) {
      try {
        first.focus({ preventScroll: true });
      } catch (e) {
        first.focus();
      }
    }
  }

  function rcCsCloseModal(modal) {
    if (!modal) return;
    if (typeof window.closeB2BModal === 'function') {
      window.closeB2BModal(modal.id);
    } else {
      modal.style.display = 'none';
      document.body.style.overflow = 'auto';
    }
    if (rcCsOpener && rcCsOpener.focus) {
      try {
        rcCsOpener.focus();
      } catch (e) {}
    }
    rcCsOpener = null;
    rcCsActiveModal = null;
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest ? e.target.closest('[data-rc-prebuy]') : null;
    if (btn) {
      e.preventDefault();
      rcCsOpenModal(btn.getAttribute('data-modal-id'), btn);
      return;
    }
    // Click on the close control or the backdrop closes the dialog.
    var modal = e.target.closest ? e.target.closest('[data-rc-cs-modal]') : null;
    if (modal && (e.target === modal || e.target.classList.contains('b2b-modal-close'))) {
      rcCsCloseModal(modal);
    }
  });

  // Escape closes the active dialog; simple focus trap keeps tabbing inside it.
  document.addEventListener('keydown', function (e) {
    if (!rcCsActiveModal) return;
    if (e.key === 'Escape') {
      rcCsCloseModal(rcCsActiveModal);
      return;
    }
    if (e.key === 'Tab') {
      var nodes = rcCsActiveModal.querySelectorAll(FOCUSABLE);
      if (!nodes.length) return;
      var firstEl = nodes[0];
      var lastEl = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === firstEl) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    }
  });

  // ---- Boot ----------------------------------------------------------------
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindAll, { once: true });
  } else {
    bindAll();
  }

  // ---- Theme editor lifecycle ---------------------------------------------
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector('[data-rc-cs-section]');
    if (root) bindSection(root);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    // Observers are shared & self-cleaning via unobserve; nothing leaks because
    // the removed nodes are simply GC'd. Reset the bound flag defensively.
    var root = e.target.querySelector('[data-rc-cs-section]');
    if (root) root.dataset.rcCsBound = '';
  });

  // On block select: force the card fully visible and scroll it into view so
  // the merchant can edit it without hovering.
  document.addEventListener('shopify:block:select', function (e) {
    var card = e.target.closest
      ? e.target.closest('[data-rc-cs-card]')
      : null;
    card = card || (e.target.querySelector && e.target.querySelector('[data-rc-cs-card]'));
    if (!card) return;
    card.classList.add('rc-cs-in', 'rc-cs-editor-active');
    card.querySelectorAll('[data-rc-cs-reveal]').forEach(function (el) {
      el.classList.add('rc-cs-in');
    });
    try {
      card.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } catch (err) {
      card.scrollIntoView();
    }
  });

  document.addEventListener('shopify:block:deselect', function (e) {
    var card = e.target.closest
      ? e.target.closest('[data-rc-cs-card]')
      : null;
    if (card) card.classList.remove('rc-cs-editor-active');
  });

  // Pause videos when tab is hidden; resume managed ones when shown.
  document.addEventListener('visibilitychange', function () {
    var hidden = document.hidden;
    document.querySelectorAll('video[data-rc-managed]').forEach(function (v) {
      if (hidden) {
        if (!v.paused) v.pause();
      }
    });
  });
})();
